// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { CalendarClock, MessageCircle } from 'lucide-react';

/**
 * Banner shown on the Dashboard reminding the "Servo de Contas" that the
 * TO-62 (Registro de Transferência de Fundos) must be submitted to Betel
 * by day 6 of each month (S-27 §27).
 *
 * Visibility windows:
 *   - days 1–3 of the month: blue/info ("prazo se aproximando")
 *   - days 4–6 of the month: amber/warning ("prazo até dia 6")
 *   - day 7+: hidden (or red if a "sent" flag is missing — not tracked yet)
 *
 * Action: WhatsApp deep-link with a pre-filled reminder message.
 */
export default function To62ReminderBanner() {
  const state = useMemo(() => buildState(new Date()), []);

  if (!state) return null;

  const monthName = state.monthName;
  const refMonth = state.refMonth; // month being reported (previous month)
  const text =
    `📒 *Lembrete TO-62*\n\n` +
    `Olá! Não esqueça de enviar o *Registro de Transferência de Fundos (TO-62)* ` +
    `referente a *${refMonth}* para Betel até o *dia 6 de ${monthName}* ` +
    `(S-27 §27).\n\n` +
    `Inclua o número de confirmação/referência da transferência. 🙏`;

  const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;

  const tone = state.level === 'warn'
    ? 'border-amber-500/50 bg-amber-50/40 dark:bg-amber-950/20'
    : 'border-sky-500/40 bg-sky-50/40 dark:bg-sky-950/20';
  const iconTone = state.level === 'warn' ? 'text-amber-600' : 'text-sky-600';

  return (
    <Card className={tone} data-testid="to62-reminder-banner">
      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <CalendarClock className={`w-6 h-6 ${iconTone} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">
            Lembrete TO-62 — entrega até <span className="underline">dia 6 de {monthName}</span>
          </p>
          <p className="text-xs text-muted-foreground">
            Envie o Registro de Transferência de Fundos de <strong>{refMonth}</strong> a Betel (S-27 §27).
          </p>
        </div>
        <Button
          size="sm"
          className="bg-[#25D366] hover:bg-[#20BD5A] text-white"
          onClick={() => window.open(waUrl, '_blank', 'noopener,noreferrer')}
          data-testid="to62-whatsapp-button"
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          Lembrar Servo de Contas
        </Button>
      </CardContent>
    </Card>
  );
}

const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

export function buildState(now) {
  const day = now.getDate();
  const monthIdx = now.getMonth();
  const year = now.getFullYear();
  const monthName = MONTHS_PT[monthIdx];

  // Reference month = previous month
  const prevDate = new Date(year, monthIdx - 1, 1);
  const refMonth = `${MONTHS_PT[prevDate.getMonth()]}/${prevDate.getFullYear()}`;

  if (day >= 1 && day <= 3) return { level: 'info', monthName, refMonth };
  if (day >= 4 && day <= 6) return { level: 'warn', monthName, refMonth };
  return null;
}
